import chroma from 'chroma-js';

const colorList = [
    '#e81a57',
    '#008b7d',
    '#547281',
    '#a4b611',
    '#0abbd3',
    '#6739b6',
    '#1f78b4',
    '#b2df8a',
    '#fb9a99',
    '#fdbf6f',
    '#cab2d6',
    '#4daf4a',
    '#984ea3',
    '#f781bf',
    '#ae017e',
];

// The preset palette offered when manually picking a color. Kept in sync with `colorList`
// so manual pickers always include the same colors used for automatic assignment.
export const PRESET_COLORS: readonly string[] = colorList;

export function resolveColor(color: ColorIndex): string {
    if (color.customColor) return color.customColor;
    return colorList[color.mainColorIndex % colorList.length];
}

export function colorsEqual(a: ColorIndex | null, b: ColorIndex | null): boolean {
    if (!a || !b) return a === b;
    if (a.customColor || b.customColor) {
        return (
            (a.customColor ?? null)?.toLowerCase() ===
            (b.customColor ?? null)?.toLowerCase()
        );
    }
    return a.mainColorIndex === b.mainColorIndex;
}

export function addAlpha(color: string, alpha: number): string {
    const [r, g, b] = chroma(color).rgb();
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function setAlpha(color: string, alpha: number): string {
    return chroma(color).alpha(alpha).css();
}

export const defaultColor = '#7F898F';
export const lightBlack = 'rgba(0,0,0,0.2)';
export const lightRed = 'rgba(255,0,0,0.2)';
export const halfBlack = 'rgba(0,0,0,0.5)';
export const halfRed = 'rgba(255,0,0,0.5)';

export class ColorIndex {
    constructor(
        readonly mainColorIndex: number,
        readonly categoryColorIndex: number,
        // Set when the color was manually picked as an arbitrary custom color, rather than one
        // of the preset palette colors. Takes precedence over `mainColorIndex` when resolving.
        readonly customColor: string | null = null
    ) {}
    toString(): string {
        return this.customColor
            ? `c${this.customColor.replace('#', '')}_${this.categoryColorIndex}`
            : `${this.mainColorIndex}_${this.categoryColorIndex}`;
    }
}

export class ColorHandler {
    private colors = new Array(colorList.length).fill(0);
    private usedColorIndexes: Set<number> = new Set();

    constructor(usedColors: ColorIndex[]) {
        usedColors.forEach(c => {
            if (c.customColor) return;
            this.colors[c.mainColorIndex]++;
            this.usedColorIndexes.add(c.mainColorIndex);
        });
    }

    getNextColorIndex(): ColorIndex {
        const distinctColorIndex = this.getDistinctColorIndex();
        this.colors[distinctColorIndex]++;
        return new ColorIndex(distinctColorIndex, this.findIndexOfLowest(this.colors));
    }

    // Marks a color as occupied, e.g. after a manual color pick or restoring a saved default,
    // so that automatic allocation for other road object types avoids reusing it right away.
    // No-op for custom colors, since they never occupy a palette slot.
    reserveColor(color: ColorIndex | null): void {
        if (!color || color.customColor) return;
        this.colors[color.mainColorIndex]++;
        this.usedColorIndexes.add(color.mainColorIndex);
    }

    decrementColor(colorIndex: ColorIndex | null): void {
        // Custom colors never occupy a palette slot, so there is nothing to release.
        if (!colorIndex || colorIndex.customColor) return;
        this.colors[colorIndex.mainColorIndex] = Math.max(
            0,
            this.colors[colorIndex.mainColorIndex] - 1
        );
        // Only free the slot once nothing else references it - the same preset color can be
        // shared by multiple road object types, and releasing it early would let the allocator
        // hand it straight back out while it is still visibly in use elsewhere.
        if (this.colors[colorIndex.mainColorIndex] === 0) {
            this.usedColorIndexes.delete(colorIndex.mainColorIndex);
        }
    }

    private getDistinctColorIndex(): number {
        let maxDifference = 0;
        let mostDistinctIndex = -1;
        for (let i = 0; i < colorList.length; i++) {
            if (!this.usedColorIndexes.has(i)) {
                let difference = this.usedColorIndexes.size > 0 ? 0 : Infinity;
                this.usedColorIndexes.forEach(usedIndex => {
                    difference += chroma.deltaE(colorList[i], colorList[usedIndex]);
                });
                if (difference > maxDifference) {
                    maxDifference = difference;
                    mostDistinctIndex = i;
                }
            }
        }
        if (mostDistinctIndex === -1) {
            mostDistinctIndex = this.findIndexOfLowest(this.colors);
        }
        this.usedColorIndexes.add(mostDistinctIndex);
        return mostDistinctIndex;
    }

    private findIndexOfLowest(array: number[]): number {
        let lowestValue = Infinity;
        let lowestIndex = 0;
        array.forEach((value, index) => {
            if (value < lowestValue) {
                lowestValue = value;
                lowestIndex = index;
            }
        });
        return lowestIndex;
    }
}
